import { useEffect, useRef } from 'react';
import type { PlayerAction } from '../playerReducer';
import type {PlayerEventCallback} from "@player/types";
import {PlayerEventType} from "../types";

export function useMediaEvents(
  mediaEl: HTMLMediaElement | null,
  dispatch: React.Dispatch<PlayerAction>,
  callbacks: {
    onTimeUpdate?: (time: number) => void;
    onPlay?: () => void;
    onPause?: () => void;
    onEnded?: () => void;
  },
  subscribersRef: React.RefObject<PlayerEventCallback[]>,
) {
  // Track whether media is currently playing so we can skip the
  // browser's low-frequency timeupdate dispatch during playback (the
  // RAF-based useFrameAccurateTime hook handles it at frame precision).
  const isPlayingRef = useRef(false);
  const waitingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!mediaEl) return;

    const onTimeUpdate = () => {
      // During playback the RAF loop already dispatches TIME_UPDATE at
      // frame-level precision.  Skipping the browser timeupdate here
      // avoids redundant state updates and re-renders.
      if (!isPlayingRef.current) {
        dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.currentTime });
      }
      callbacks.onTimeUpdate?.(mediaEl.currentTime);

      // Send playback event to subcribers
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.TIME_UPDATE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
      }
    };

    const onPlay = () => {
      isPlayingRef.current = true;
      dispatch({ type: 'PLAY' });
      callbacks.onPlay?.();

      // Send playback event to subcribers
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.PLAY, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
      }
    };

    const onPause = () => {
      isPlayingRef.current = false;
      dispatch({ type: 'PAUSE' });
      callbacks.onPause?.();

      // Send playback event to subcribers
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.PAUSE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
      }
    };

    const onLoadedMetadata = () => {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
      dispatch({ type: 'MEDIA_READY' });
    };

    const onDurationChange = () => {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
    };

    const onProgress = () => {
      if (mediaEl.buffered.length > 0) {
        dispatch({
          type: 'BUFFER_UPDATE',
          bufferedEnd: mediaEl.buffered.end(mediaEl.buffered.length - 1),
        });
      }
    };

    const onWaiting = () => {
      // Debounce: only show the spinner if the waiting state persists
      // for more than 200ms. Short seeks (frame steps, small scrubs)
      // complete in <100ms and shouldn't flash the spinner.
      if (waitingTimerRef.current !== null) return;
      waitingTimerRef.current = setTimeout(() => {
        waitingTimerRef.current = null;
        dispatch({ type: 'SET_BUFFERING', isBuffering: true });
      }, 200);
    };
    const onCanPlay = () => {
      // Cancel the debounce timer if waiting resolves quickly.
      if (waitingTimerRef.current !== null) {
        clearTimeout(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
      dispatch({ type: 'SET_BUFFERING', isBuffering: false });
    };
    const onEnded = () => {
      isPlayingRef.current = false;
      // Snap the playhead to the very end so the UI shows the full duration.
      dispatch({ type: 'TIME_UPDATE', currentTime: mediaEl.duration });
      dispatch({ type: 'MEDIA_ENDED' });
      // The browser's native loop prevents the 'ended' event, so this
      // only fires when looping is off and playback genuinely ended.
      callbacks.onEnded?.();

      // Send playback event to subcribers
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.PAUSE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
      }
    };

    const onVolumeChange = () => {
      dispatch({ type: 'SET_VOLUME', volume: mediaEl.volume });
    };

    const onRateChange = () => {
      dispatch({ type: 'SET_PLAYBACK_RATE', rate: mediaEl.playbackRate });

      // Emit SPEED_CHANGE (not SEEK — a rate change is not a seek).
      // The prior code emitted SEEK here which caused sync followers to
      // treat every rate change as a seek target, corrupting position
      // sync during playback rate changes.
      for (const subscriber of subscribersRef.current) {
        subscriber({ type: PlayerEventType.SPEED_CHANGE, currentTime: mediaEl.currentTime, playbackRate: mediaEl.playbackRate, isPaused: mediaEl.paused });
      }
    };

    mediaEl.addEventListener('timeupdate', onTimeUpdate);
    mediaEl.addEventListener('play', onPlay);
    mediaEl.addEventListener('pause', onPause);
    mediaEl.addEventListener('loadedmetadata', onLoadedMetadata);
    mediaEl.addEventListener('durationchange', onDurationChange);
    mediaEl.addEventListener('progress', onProgress);
    mediaEl.addEventListener('waiting', onWaiting);
    mediaEl.addEventListener('canplay', onCanPlay);
    mediaEl.addEventListener('ended', onEnded);
    mediaEl.addEventListener('volumechange', onVolumeChange);
    mediaEl.addEventListener('ratechange', onRateChange);

    // If metadata already loaded
    if (mediaEl.readyState >= 1) {
      dispatch({ type: 'DURATION_SET', duration: mediaEl.duration });
      dispatch({ type: 'MEDIA_READY' });
    }

    return () => {
      if (waitingTimerRef.current !== null) {
        clearTimeout(waitingTimerRef.current);
        waitingTimerRef.current = null;
      }
      mediaEl.removeEventListener('timeupdate', onTimeUpdate);
      mediaEl.removeEventListener('play', onPlay);
      mediaEl.removeEventListener('pause', onPause);
      mediaEl.removeEventListener('loadedmetadata', onLoadedMetadata);
      mediaEl.removeEventListener('durationchange', onDurationChange);
      mediaEl.removeEventListener('progress', onProgress);
      mediaEl.removeEventListener('waiting', onWaiting);
      mediaEl.removeEventListener('canplay', onCanPlay);
      mediaEl.removeEventListener('ended', onEnded);
      mediaEl.removeEventListener('volumechange', onVolumeChange);
      mediaEl.removeEventListener('ratechange', onRateChange);
    };
  }, [mediaEl, dispatch, callbacks, subscribersRef]);
}
